import React, { useState } from 'react';
import { Route, Plus, X, Check, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import UberLocationPicker from '../UberLocationPicker';
import { useTranslation } from 'react-i18next';

const StopsCard = ({ onComplete, initialStops = [] }: any) => {
  const { t } = useTranslation();
  const [stops, setStops] = useState(initialStops);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const handleAddStop = () => {
    setShowLocationPicker(true);
  };

  const handleLocationSelect = (location: any) => {
    setStops([...stops, { location, id: Date.now() }]);
    setShowLocationPicker(false);
  };

  const handleRemoveStop = (id: any) => {
    setStops(stops.filter((stop: any) => stop.id !== id));
  };

  const handleComplete = () => {
    onComplete(stops);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4"
    >
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
        
        <div className="relative bg-background dark:bg-slate-800 rounded-2xl p-4 border border-border dark:border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center">
                <Route size={20} className="text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground dark:text-slate-200">{t('stopsCard.stopsAlongTheWay', 'Stopp underveis')}</h4>
                <p className="text-xs text-muted-foreground">Add intermediate stops (optional)</p>
              </div>
            </div>
            {stops.length > 0 && (
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check size={16} className="text-green-600 dark:text-green-400" />
              </div>
            )}
          </div>

          {/* List of stops */}
          <AnimatePresence>
            {stops.length > 0 && (
              <div className="space-y-2 mb-3">
                {stops.map((stop: any, index: any) => (
                  <motion.div
                    key={stop.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="flex items-center space-x-3 p-3 bg-muted/50 dark:bg-slate-900/50 rounded-xl group"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-teal-400 flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground dark:text-slate-300 truncate">
                        {stop.location}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveStop(stop.id)}
                      className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X size={16} className="text-red-600 dark:text-red-400" />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>

          {/* Add stop button */}
          <button
            onClick={handleAddStop}
            className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-green-500/50 transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <Plus size={20} />
            <span>Add stop</span>
          </button>

          {/* Continue button */}
          <button
            onClick={handleComplete}
            className="w-full mt-3 py-3 px-4 bg-slate-200 dark:bg-slate-700 text-foreground dark:text-slate-300 rounded-xl font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-all"
          >
            {stops.length > 0 ? `Fortsett med ${stops.length} stopp` : 'Fortsett uten stopp'}
          </button>
        </div>
      </div>

      {/* Location Picker Modal */}
      <UberLocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelect={handleLocationSelect}
        title={t('chat.addStopBtn')}
      />
    </motion.div>
  );
};

export default StopsCard;
